import plur from 'plur'
import {useEffect, useMemo} from 'react'
import {useLinkClickHandler, useParams} from 'react-router-dom'

import {useAppSelector} from '../../../../../../hooks/react-redux'
import Routes, {getHref} from '../../../../../../routes'
import {restApi} from '../../../../../../services/rest'
import {toBuildId} from '../../../../../../types'
import {subscribeOnBuildTypeEvents} from '../../../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_ADDED_TO_QUEUE,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../../../utils/subscriptionEvents'
import IconButton from '../../../../../common/IconButton/IconButton'
import type {pipelineHeadFragment$key} from '../../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineHead, usePipelineHeadId, usePipelineId} from '../../../hooks/pipeline'

import {getBuildDuration, getBuildTimeInQueue} from './PipelineNavigation.selectors'
import {PipelineNavigationItem} from './PipelineNavigationItem/PipelineNavigationItem'

import styles from './PipelineNavigation.css'

const DISPLAY_RUNS_COUNT = 15
const maxRunsAroundCurrent = (DISPLAY_RUNS_COUNT - 1) / 2

type Props = {pipelineHeadKey: pipelineHeadFragment$key | null}
export function PipelineNavigation({pipelineHeadKey}: Props) {
  const headId = usePipelineHeadId(pipelineHeadKey)
  const runIdParam = useParams().runId ?? ''
  const runId = toBuildId(runIdParam)
  const {runIds, refetch} = restApi.endpoints.getAllBuildsNormalized.useQuery(
    {
      locator: `defaultFilter:false,buildType:(id:${headId})`,
      fields:
        'build(id,number,queuePosition,state,queuedDate,startDate,running-info(elapsedSeconds),finishDate,status,canceledInfo,failedToStart)',
    },
    {selectFromResult: ({data}) => ({runIds: data?.result})},
  )
  const head = usePipelineHead(pipelineHeadKey)
  const headInternalId = head?.internalId

  useEffect(
    () =>
      headInternalId != null
        ? subscribeOnBuildTypeEvents(
            headInternalId,
            [
              BUILD_TYPE_ADDED_TO_QUEUE,
              BUILD_TYPE_REMOVED_FROM_QUEUE,
              BUILD_STARTED,
              BUILD_FINISHED,
              BUILD_INTERRUPTED,
            ],
            refetch,
          )
        : undefined,
    [headInternalId, refetch],
  )

  const currentRunIndex = runIds?.indexOf(runId) ?? 0
  const shownRunIds = useMemo(() => {
    if (runIds == null) {
      return []
    }
    const maxStartIndex = runIds.length - DISPLAY_RUNS_COUNT
    const startIndex = Math.max(0, Math.min(maxStartIndex, currentRunIndex - maxRunsAroundCurrent))
    return runIds.slice(startIndex, startIndex + DISPLAY_RUNS_COUNT).reverse()
  }, [currentRunIndex, runIds])
  const maxDuration = useAppSelector(state =>
    Math.max(...shownRunIds.map(id => getBuildDuration(state, id))),
  )
  const maxTimeInQueue = useAppSelector(state =>
    Math.max(...shownRunIds.map(id => getBuildTimeInQueue(state, id))),
  )
  const pipelineId = usePipelineId()
  const prevId = runIds?.[currentRunIndex + 1]
  const prevHref = getHref(Routes.PIPELINE_RUN, {id: pipelineId, runId: String(prevId)})
  const prevOnClick = useLinkClickHandler(prevHref)
  const nextId = runIds?.[currentRunIndex - 1]
  const nextHref = getHref(Routes.PIPELINE_RUN, {id: pipelineId, runId: String(nextId)})
  const nextOnClick = useLinkClickHandler(nextHref)
  return runIds != null ? (
    <div className={styles.container}>
      <div className={styles.chart}>
        {shownRunIds.map(id => (
          <PipelineNavigationItem
            key={id}
            active={id === runId}
            runId={id}
            maxDuration={maxDuration}
            maxTimeInQueue={maxTimeInQueue}
          />
        ))}
      </div>
      <div className={styles.prevNext}>
        <IconButton
          className={styles.prevNextButton}
          icon="chevron-left"
          href={prevHref}
          onClick={prevOnClick}
          disabled={prevId == null}
        />
        {`${runIds.length - currentRunIndex} of ${runIds.length} ${plur('run', runIds.length)}`}
        <IconButton
          className={styles.prevNextButton}
          icon="chevron-right"
          href={nextHref}
          onClick={nextOnClick}
          disabled={nextId == null}
        />
      </div>
    </div>
  ) : null
}
